import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";
import * as Icons from "lucide-react";
import { SOCIAL_MEDIA_SERVICES, SOCIAL_MEDIA_PROCESS } from "@/lib/data";
import { Reveal, RevealStagger, StaggerItem, Cascade } from "@/components/Reveal";
import { useDetailModal } from "@/components/DetailModalContext";

const Icon = ({ name, className }) => {
  const Cmp = Icons[name] || Icons.Circle;
  return <Cmp className={className} strokeWidth={1.5} />;
};

const ACCENT_STYLES = {
  orange: {
    iconBg: "bg-orange/10 text-orange",
    iconHover: "group-hover:bg-orange group-hover:text-white",
    glow: "from-orange/20",
    ring: "group-hover:border-orange/30",
  },
  purple: {
    iconBg: "bg-purple-100 text-purple-600",
    iconHover: "group-hover:bg-purple-600 group-hover:text-white",
    glow: "from-purple-200",
    ring: "group-hover:border-purple-300",
  },
  green: {
    iconBg: "bg-green-100 text-green-600",
    iconHover: "group-hover:bg-green-600 group-hover:text-white",
    glow: "from-green-200",
    ring: "group-hover:border-green-300",
  },
};

export default function SocialMedia() {
  const { openDetail } = useDetailModal();

  return (
    <section id="sosyal-medya" className="relative scroll-mt-24 overflow-hidden" data-testid="social-media">
      {/* Animated gradient background */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <motion.div
          className="absolute -top-40 right-0 h-[500px] w-[500px] rounded-full bg-gradient-to-br from-orange/15 via-purple-200/20 to-transparent blur-3xl"
          animate={{ x: [0, 60, 0], y: [0, 40, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-1/2 -left-40 h-[400px] w-[400px] rounded-full bg-gradient-to-tr from-purple-200/15 via-green-200/15 to-transparent blur-3xl"
          animate={{ x: [0, 50, 0], y: [0, -30, 0] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />
      </div>

      <div className="container-shell">
        {/* Section header */}
        <Reveal className="max-w-2xl">
          <p className="overline">Sosyal Medya ve Marketing</p>
          <h2 className="heading-lg mt-4">
            <Cascade text="Markanızın dijital dünyada {öne çıkması} için." />
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-ink/60">
            Sosyal medya yönetiminden profesyonel çekimlere, Google SEO çalışmalarından
            içerik üretimine kadar markanızı dijitalde büyütüyoruz.
          </p>
        </Reveal>

        {/* Service cards — click to open DetailModal */}
        <RevealStagger className="mt-[56px] grid grid-cols-1 gap-[32px] md:grid-cols-3">
          {SOCIAL_MEDIA_SERVICES.map((s) => {
            const accent = ACCENT_STYLES[s.accent] || ACCENT_STYLES.orange;
            return (
              <StaggerItem key={s.title}>
                <div
                  className={`group h-full cursor-pointer overflow-hidden rounded-3xl border border-ink/5 bg-white p-8 shadow-[0_2px_10px_rgba(32,32,32,0.04)] transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-2 hover:shadow-[0_24px_50px_rgba(32,32,32,0.10)] ${accent.ring}`}
                  onClick={() => openDetail(s)}
                  data-testid={`social-card-${s.title}`}
                >
                  {/* Glow effect on hover */}
                  <div
                    className={`absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br ${accent.glow} to-transparent opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100`}
                  />

                  <div className="relative">
                    {/* Icon */}
                    <span
                      className={`flex h-14 w-14 items-center justify-center rounded-2xl ${accent.iconBg} ${accent.iconHover} transition-colors duration-500`}
                    >
                      <Icon name={s.icon} className="h-7 w-7" />
                    </span>

                    <h3 className="mt-6 text-xl font-bold tracking-tight text-ink">{s.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-ink/60">{s.desc}</p>

                    <button
                      onClick={(e) => { e.stopPropagation(); openDetail(s); }}
                      className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-ink transition-colors hover:text-orange"
                    >
                      Detaylı İncele
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                  </div>
                </div>
              </StaggerItem>
            );
          })}
        </RevealStagger>

        {/* Process steps */}
        <Reveal className="mt-[80px]">
          <div className="flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-orange" />
            <p className="overline">Nasıl Çalışıyoruz</p>
          </div>
          <h3 className="mt-4 text-2xl font-bold tracking-tight text-ink md:text-3xl">
            4 adımda markanızı dijitalde büyütelim
          </h3>
        </Reveal>

        <RevealStagger className="mt-[40px] grid grid-cols-1 gap-[24px] sm:grid-cols-2 lg:grid-cols-4">
          {SOCIAL_MEDIA_PROCESS.map((p, i) => (
            <StaggerItem key={p.step}>
              <div className="group relative h-full rounded-2xl border border-ink/5 bg-gradient-to-b from-white to-gray-50/50 p-6 transition-all duration-500 hover:border-orange/20 hover:shadow-[0_12px_30px_rgba(238,127,46,0.08)]">
                {/* Step number */}
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-extrabold text-orange/20 transition-colors duration-500 group-hover:text-orange/40">
                    {p.step}
                  </span>
                  <div className="h-px flex-1 bg-ink/10 transition-colors duration-500 group-hover:bg-orange/20" />
                </div>

                <h4 className="mt-4 text-lg font-bold tracking-tight text-ink">{p.title}</h4>
                <p className="mt-2 text-sm leading-relaxed text-ink/60">{p.desc}</p>

                {/* Connecting arrow for desktop */}
                {i < SOCIAL_MEDIA_PROCESS.length - 1 && (
                  <div className="absolute -right-3 top-1/2 hidden -translate-y-1/2 lg:block">
                    <ArrowUpRight className="h-5 w-5 text-ink/15" />
                  </div>
                )}
              </div>
            </StaggerItem>
          ))}
        </RevealStagger>
      </div>
    </section>
  );
}
